// AnimalForm.tsx
import React, { useState } from 'react'
import { Box } from '@mui/material'
import { EmtButton, EmtSeparator, EmtTypography } from 'emt-components-v2'
import { useTranslation } from 'next-i18next'
import VariantImages from '@/assets/VariantImages'

// Custom inputs
import AnimalFormInputs from './AnimalFormInputs'
import ImageUploader from './ImageUploader'
import SwitchChoices from '../../Forms/SwitchChoices'
import { Validator, convertToAPIReadyBase64Async, scrollToFirstError } from '@/utils'
import { Main } from '@/assets/configs'
import VolunteerPetProfileOptions from '@/components/Profile/VolunteerPetProfileOptions'

import { containsPhoneNumber } from '@/utils/validationUtils'

import { useEMTState } from '@/store'

import { Gender } from '@/dataV3/Common/Gender'
import { AnimalSpecies } from '@/dataV3/Animal/AnimalSpecies'

import { usePetsittingMode } from '@/store/usePetsittingMode'

export interface AnimalData {
  acquisitionStatus?: {
    haveAnimalRequiredOptions: boolean
  }
  uuid: string
  displayName: string
  breedSlug: string
  birthDate: string
  sizeSlug: string
  speciesSlug?: AnimalSpecies | null
  gender: Gender | null
  sterilized: boolean | null
  description: string
  selectedRadio: AnimalSpecies | null
  mainPicture: string | null
  mainPictureUrl?: string
  animalPictures: Array<string>
  icon: string
  showVolunteer?: boolean
  affinities: {
    niceWithChildren: boolean
    niceWithDogs: boolean
    niceWithCats: boolean
  }
  times: {
    entrustFewHours: boolean
    entrustDayNight: boolean
    entrustWeekendHoliday: boolean
  }
  availability: {
    availabilityWeekDay: boolean
    availabilityWeekNight: boolean
    availabilityWeekend: boolean
  }
  needs: {
    needTrips: boolean
    needExercise: boolean
    needPlay: boolean
    needKeep: boolean
    needTakeCare: boolean
    needLove: boolean
  }
}

export interface AnimalFormProps {
  initialValues?: AnimalData
  onSubmit: (values: AnimalData) => void
  onCancel?: () => void
  isLoading?: boolean
  color?: 'purple' | 'primary'
  hideButtons?: boolean
  onFieldChange?: (field: string, value: any) => void
  canCancel?: boolean
  variant?: 'signup' | 'base'
  defaultImages?: boolean
  defaultDescription?: string | null
  showVolunteerSection?: boolean // NEW PROP
}

const AnimalForm: React.FC<AnimalFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
  color = 'purple',
  hideButtons = false,
  onFieldChange,
  canCancel = true,
  variant = 'base',
  defaultImages = false,
  defaultDescription = null,
  showVolunteerSection = true, // default true for backward compatibility
}) => {
  const { t } = useTranslation('connection')
  const { t: tc } = useTranslation('common')
  const [formIsSent, setFormIsSent] = useState(false)
  const { disponibilityChoices, dogAffinityChoices } = useEMTState().configuration

  const { isPetsittingMode } = usePetsittingMode()

  const availability = disponibilityChoices.find((c) => c.slug === 'disponibility')
  const times = disponibilityChoices.find((c) => c.slug === 'attention')
  const needs = dogAffinityChoices.find((c) => c.slug === 'needs')
  const affinities = dogAffinityChoices.find((c) => c.slug === 'affinities')

  const [values, setValues] = useState<AnimalData>(
    initialValues || {
      uuid: '',
      displayName: '',
      breedSlug: '',
      birthDate: '',
      sizeSlug: '',
      gender: null,
      sterilized: null,
      description: '',
      selectedRadio: AnimalSpecies.dog,
      mainPicture: null,
      animalPictures: [],
      icon: '',
      showVolunteer: true,
      affinities: {
        niceWithChildren: false,
        niceWithDogs: false,
        niceWithCats: false,
      },
      times: {
        entrustFewHours: false,
        entrustDayNight: false,
        entrustWeekendHoliday: false,
      },
      availability: {
        availabilityWeekDay: false,
        availabilityWeekNight: false,
        availabilityWeekend: false,
      },
      needs: {
        needTrips: false,
        needExercise: false,
        needPlay: false,
        needKeep: false,
        needTakeCare: false,
        needLove: false,
      },
    },
  )

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [volunteerErrors, setVolunteerErrors] = useState<Record<string, string>>({})
  const [isNewAvatar, setIsNewAvatar] = useState<boolean>(false)
  const [newImageIndexes, setNewImageIndexes] = useState<Set<number>>(new Set())

  // Update values useEffect to reset new image tracking when initialValues change
  React.useEffect(() => {
    if (initialValues) {
      setIsNewAvatar(false)
      setNewImageIndexes(new Set())
    }
  }, [initialValues])

  const onFieldChangeHandler = (key: keyof AnimalData, value: any) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    if (formIsSent) checkField(key, value)
    onFieldChange?.(key, value)
  }

  const checkField = (key: keyof AnimalData, value: any = values[key]): boolean => {
    let error = ''

    switch (key) {
      case 'displayName':
        if (!value || containsPhoneNumber(value)) {
          error = t('sign-up.details.errors.invalidName') || 'Name required'
        } else if (value.length > 20) {
          error = t('sign-up.details.errors.nameTooLong') || 'Name is too long'
        }
        break
      case 'breedSlug':
        if (!value)
          error =
            values.selectedRadio === AnimalSpecies.nac
              ? t('create-your-profile.availability.errors.specie')
              : t('create-your-profile.availability.errors.dogBreed') || 'Breed required'
        break
      case 'birthDate':
        if (!value) error = t('create-your-profile.availability.errors.birthdayDate') || 'Birth date required'
        break
      case 'sizeSlug':
        if (!value) error = t('create-your-profile.availability.errors.dogSize') || 'Size required'
        break
      case 'gender':
        if (!value) error = t('create-your-profile.availability.errors.dogSex') || 'Gender required'
        break
      case 'sterilized':
        if (value === null && (values.selectedRadio === 'dog' || values.selectedRadio === 'cat'))
          error = t('create-your-profile.availability.errors.sterilized') || 'Sterilized required'
        break
      case 'description':
        if (!value && !defaultDescription) {
          error = t('create-your-profile.description.errors.descriptionRequired.dogowner') || 'Description required'
        } else if (
          !Validator.validate(value, Validator.defaultValidateTypes.lengthMin(Main.DescriptionMinLength)) &&
          !defaultDescription
        ) {
          error =
            t('create-your-profile.description.errors.descriptionInvalid', {
              length: Main.DescriptionMinLength,
            }) || `Minimum length is ${Main.DescriptionMinLength}`
        }
        break
      case 'selectedRadio':
        if (!value) error = t('create-your-profile.availability.errors.petType') || 'Pet type required'
        break
      case 'mainPicture':
        if (!value && !defaultImages) error = t('create-your-profile.address-photo.errors.avatar') || 'Avatar required'
        break
      default:
        break
    }

    setErrors((prev) => ({ ...prev, [key]: error }))
    return !error
  }

  const checkVolunteerField = (key: keyof AnimalData, value: any = volunteerChoices[key]): boolean => {
    let error = ''

    switch (key) {
      case 'times':
      case 'availability':
      case 'needs':
        if (!value || !Object.values(value).some((v) => v === true)) {
          error = t(`sign-up.details.errors.atLeastOne`) || 'At least one choice required'
        }
        break
      default:
        break
    }

    setVolunteerErrors((prev) => ({ ...prev, [key]: error }))
    return !error
  }

  const checkVolunteerSection = (): boolean => {
    let valid = true
    ;(['times', 'availability', 'needs'] as Array<keyof AnimalData>).forEach((key) => {
      if (!checkVolunteerField(key)) valid = false
    })
    return valid
  }

  const checkForm = () => {
    let valid = true
    ;(Object.keys(values) as Array<keyof AnimalData>).forEach((key) => {
      if (!checkField(key)) valid = false
    })
    if (showVolunteerSection && showVolunteer && values.selectedRadio === 'dog' && !checkVolunteerSection())
      valid = false
    return valid
  }

  const handleSubmit = async () => {
    setFormIsSent(true)
    if (!checkForm()) {
      scrollToFirstError()
      return
    }

    const getDefaultImage = async () => {
      if (!defaultImages) return null
      let img:
        | typeof VariantImages.DefaultDog
        | typeof VariantImages.DefaultCat
        | typeof VariantImages.DefaultNac
        | null = null
      switch (values.selectedRadio) {
        case AnimalSpecies.dog:
          img = VariantImages.DefaultDog
          break
        case AnimalSpecies.cat:
          img = VariantImages.DefaultCat
          break
        case AnimalSpecies.nac:
          img = VariantImages.DefaultNac
          break
        default:
          img = null
      }
      if (img) {
        return await convertToAPIReadyBase64Async(img.default.src, true)
      }
      return null
    }

    let mainPictureValue: string | null = null
    if (isNewAvatar) {
      mainPictureValue = values.mainPicture
    } else if (values.mainPicture) {
      mainPictureValue = values.mainPicture
    } else if (defaultImages) {
      mainPictureValue = await getDefaultImage()
    } else {
      mainPictureValue = null
    }

    const submissionData = {
      ...values,
      showVolunteer,
      mainPicture: mainPictureValue,
      description: values.description || defaultDescription || '',
      animalPictures: values.animalPictures.filter((_, index) => newImageIndexes.has(index)),
    }

    onSubmit(submissionData)
  }

  // Volunteer state
  const [showVolunteer, setShowVolunteer] = useState(initialValues?.showVolunteer || !isPetsittingMode || false)
  const [volunteerChoices, setVolunteerChoices] = useState({
    times: initialValues?.times || {},
    availability: initialValues?.availability || {},
    needs: initialValues?.needs || {},
  })

  const onToggleVolunteer = () => {
    setShowVolunteer((prev) => {
      const next = !prev
      if (!next) {
        // clear volunteer errors when turning off
        setVolunteerErrors({})
      }
      return next
    })
  }

  const handleVolunteerChoice = (category: 'times' | 'availability' | 'needs', slug: string, value: boolean) => {
    setVolunteerChoices((prev) => ({
      ...prev,
      [category]: { ...prev[category], [slug]: value },
    }))
    checkVolunteerField(category, {
      ...volunteerChoices[category],
      [slug]: value,
    })
    setValues((prev) => ({
      ...prev,
      [category]: { ...prev[category], [slug]: value },
    }))
  }

  // Modify image handlers
  const handleAvatarChange = (avatar: string | null) => {
    setIsNewAvatar(true)
    onFieldChangeHandler('mainPicture', avatar)
  }

  const handleImagesChange = (images: string[], index?: number) => {
    if (typeof index === 'number') {
      setNewImageIndexes((prev) => new Set([...prev, index]))
    }
    onFieldChangeHandler('animalPictures', images)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <AnimalFormInputs
        values={values}
        onFieldChange={onFieldChangeHandler}
        errors={errors}
        color={color}
        defaultDescription={defaultDescription}
      />

      <Box>
        <EmtTypography variant="body1" fontWeight={700} mb={0.5}>
          {t('create-your-profile-petowner.pet.photosTitle') || 'Photos'}
          {defaultImages && <span style={{ fontWeight: '400', fontSize: '14px', color: '#77757F' }}> (Optionnel)</span>}
        </EmtTypography>
        <ImageUploader
          images={values.animalPictures}
          avatar={values.mainPicture}
          errors={errors}
          onImagesChange={handleImagesChange}
          onAvatarChange={handleAvatarChange}
          color="primary"
          variant={variant}
        />
      </Box>

      <SwitchChoices
        handleChoice={(slug, val) => setValues((v) => ({ ...v, affinities: { ...v.affinities, [slug]: val } }))}
        selectedChoices={values.affinities}
        choices={
          affinities?.types?.map((e) => ({
            slug: e.slug,
            name: e.sentence.ownerSentence.inscriptionSentence,
            iconClass: e.icon?.className,
          })) || []
        }
        title={t('create-your-profile-petowner.pet.switchTitle') || 'Additional Options'}
        color={color}
      />

      {!isPetsittingMode && <EmtSeparator />}

      {!isPetsittingMode && showVolunteerSection && values.selectedRadio === 'dog' && (
        <VolunteerPetProfileOptions
          showVolunteer={showVolunteer}
          onToggleVolunteer={onToggleVolunteer}
          volunteerChoices={volunteerChoices}
          handleVolunteerChoice={handleVolunteerChoice}
          errors={volunteerErrors}
          timeChoices={
            times?.types.map((e) => ({
              slug: e.slug,
              name: e.sentence.ownerSentence.inscriptionSentence,
            })) || []
          }
          availabilityChoices={
            availability?.types.map((e) => ({
              slug: e.slug,
              name: e.sentence.ownerSentence.inscriptionSentence,
            })) || []
          }
          needsChoices={
            needs?.types.map((e) => ({
              slug: e.slug,
              name: e.sentence.ownerSentence.inscriptionSentence,
              iconClass: e.icon?.className,
            })) || []
          }
        />
      )}

      {!hideButtons && (
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
          {onCancel && canCancel && (
            <EmtButton fullWidth variant="outlined" color={color} onClick={onCancel}>
              {tc('cancel') || 'Annuler'}
            </EmtButton>
          )}
          <EmtButton fullWidth variant="contained" color={color} onClick={handleSubmit} loader={isLoading}>
            {tc('save') || 'Enregistrer'}
          </EmtButton>
        </Box>
      )}
    </Box>
  )
}

export default AnimalForm
